import { getLogger } from '../../diagnostics/logger';
import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { ENSURE_SELECTED_COMPANY, INITIAL_AUTHENTICATION, LOAD_SELECTED_COMPANY } from '../../actions/actionTypes';
import { Action } from '../../actions/commonActions';
import {
  loadSelectedCompany,
  loadSelectedCompanyFailure,
  loadSelectedCompanySuccess,
} from '../../actions/companyActions';
import { clearGridFilter } from '../../actions/gridActions';
import { clearSims } from '../../actions/simActions';
import { clearSimSubmissions } from '../../actions/simSubmissionActions';
import * as companyApi from '../../api/companyApi';
import * as companyStatusApi from '../../api/companyStatusApi';
import { Loadable } from '../../models';
import { Company } from '../../models/company';
import { CompanyStatus } from '../../models/companyStatus';
import { selectCompany } from '../../selectors/filter';

const log = getLogger('simmanagement.sagas.login');

/**
 * Ensure that the selected company is present in the store and will initiate a server load if not.
 * @param action The action that invoked the saga.
 */
export function* ensureSelectedCompanySaga(action: Action<number>) {
  if (action.payload) {
    const company: Loadable<Company> = yield select(selectCompany);
    const companyId = action.payload;
    if (company.id !== companyId || (company.expiresOn && company.expiresOn < new Date())) {
      yield put(loadSelectedCompany(companyId));
    }
  }
}

/**
 * Load the selected company.
 * @param action The action that invoked the saga.
 */
export function* loadSelectedCompanySaga(action: Action<number>) {
  if (action.payload) {
    const companyId = action.payload;
    const oldCompany: Loadable<Company> = yield select(selectCompany);
    if (oldCompany.id !== companyId) {
      yield put(clearGridFilter('simsList'));
      yield put(clearSims());
      yield put(clearSimSubmissions());
    }

    try {
      const newCompany: Company = yield call(companyApi.getById, companyId);
      newCompany.companyStatus = yield call(companyStatusApi.getById, companyId);
      yield put(loadSelectedCompanySuccess(newCompany));
    } catch (error: any) {
      yield put(loadSelectedCompanyFailure(companyId, error));
    }
  }
}

/*
 * Initialize all sagas related to companies
 */
export function* initCompanySagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(ENSURE_SELECTED_COMPANY, ensureSelectedCompanySaga);
  yield takeLatest(LOAD_SELECTED_COMPANY, loadSelectedCompanySaga);
}
